import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Activity, Loader2, ArrowRight } from "lucide-react";

export default function AuthPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.user, data.token);
      toast.success("Welcome back!");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-neutral-950 text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1559136555-9303baea8ebd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center">
            <Activity className="h-6 w-6 text-white" />
          </div>
          <span className="font-heading text-xl font-bold">ABS Pulse</span>
        </div>
        <div className="relative space-y-4">
          <h1 className="font-heading text-4xl xl:text-5xl font-bold tracking-tight leading-tight">
            Every day adds to our story.
          </h1>
          <p className="text-slate-300 text-lg max-w-md">
            Share what you accomplished, what you worked on, and what needs
            attention. Every update keeps our team connected, helps us
            understand what's moving, and ensures the work you do becomes part
            of the bigger picture.
          </p>
        </div>
        <p className="relative text-sm text-slate-400 font-mono">
          Your work. Your update. Our progress.
        </p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center">
              <Activity className="h-6 w-6 text-white" />
            </div>
            <span className="font-heading text-xl font-bold">ABS Pulse</span>
          </div>

          <h2 className="font-heading text-3xl font-bold tracking-tight">
            Welcome to ABS Pulse
          </h2>
          <p className="text-muted-foreground mt-2 mb-8">
            Sign in and share your day with the team.
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="auth-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="auth-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <p
                className="text-sm text-destructive font-medium"
                data-testid="auth-error"
              >
                {error}
              </p>
            )}

            <Button
              type="submit"
              className="w-full rounded-lg h-11"
              data-testid="auth-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Sign in
                  <ArrowRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            Accounts are created by your administrator. Ask them for access if you
            can't sign in.
          </p>
          <p className="text-[10px] leading-relaxed text-muted-foreground mt-8 text-center" data-testid="auth-footer">
            © 2026 Aabasoft. All Rights Reserved. | ABS Pulse — Proprietary &amp; Confidential | Unauthorized copying, sharing, reproduction, or use is prohibited.
          </p>
        </div>
      </div>
    </div>
  );
}
